import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IMomentTemplate,
  isMomentTemplateTemplateId,
  MomentTemplateStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  FormHelperText,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { capitalize, isEqual } from 'lodash';
import {
  Clock,
  Component,
  Image,
  LucideIcon as Icon,
  Mail,
  User,
  Users
} from 'lucide-react';
import moment from 'moment-timezone';
import {
  ComponentType,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DeleteButton } from '../../../../components/DeletionConfirmation';
import { PageLoader } from '../../../../components/Loader';
import { TokenDefinition } from '../../../../components/MarkdownText';
import { SomethingWentWrongWithReport } from '../../../../components/SomethingWentWrong';
import { useBlockOnRouteChange } from '../../../../hooks/useBlockOnRouteChange';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useModel } from '../../../../hooks/useModel';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../services/features';
import { updateDataInDoc } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { formatDatePrecise, toMoment } from '../../../../services/time';
import { useTokenDefinitionsForMoment } from '../../../Experiences/components/Builders/TaskBuilder/components/BlockSettings/common';
import { MomentTemplateStatusChip } from '../../components/StatusChip';
import {
  deleteMomentTemplate,
  duplicateMomentTemplate,
  saveMomentTemplate,
  updateMomentTemplateStatus,
  useMomentTemplateById
} from '../../services';
import {
  duplicateMomentTemplateTemplate,
  useMomentTemplatesTemplateById
} from '../../services/templateTemplates';
import {
  EditSectionAudience,
  ViewSectionAudience
} from './components/Audience';
import {
  EditSectionCategories,
  ViewSectionCategories
} from './components/Categories';
import { EditSectionContent, ViewSectionContent } from './components/Content';
import { EditSectionFrom, ViewSectionFrom } from './components/From';
import {
  EditSectionSendDate,
  ViewSectionSendDate
} from './components/SendDate';
import { EditSectionSubject, ViewSectionSubject } from './components/Subject';
import { EditComponentProps, ViewComponentProps } from './types';

const MIN_TITLE_LENGTH = 3;
type Validators =
  | 'NO_TITLE'
  | 'NO_AUDIENCE'
  | 'NO_FROM_NAME'
  | 'NO_REPLYTO_EMAIL'
  | 'PAST_SEND_DATE'
  | 'NO_SUBJECT'
  | 'NO_TA_BLOCKS'
  | 'CATEGORIES';

const EditableTitle = ({
  d,
  editActive,
  otherEditActive,
  onSwitchMode,
  onSave,
  errorHelper,
  model,
  setModel,
  hasChanges,
  isTemplateTemplates
}: {
  d: Doc<IMomentTemplate>;
  onSave: (update: Partial<IMomentTemplate>) => Promise<any>;
  editActive: boolean;
  otherEditActive: boolean;
  onSwitchMode: () => void;
  errorHelper: React.ReactNode;
  model: Doc<IMomentTemplate>;
  setModel: React.Dispatch<React.SetStateAction<Doc<IMomentTemplate>>>;
  hasChanges: boolean;
  isTemplateTemplates: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const [x, setX] = useState(d.data.title);
  useEffect(() => {
    setX(d.data.title);
  }, [d]);

  return (
    <>
      {!editActive ? (
        <div>
          <FlexContainer>
            <Typography variant="h3">{d.data.title}</Typography>
            {d.data.status !== MomentTemplateStatus.COMPLETE && (
              <Button
                disabled={
                  otherEditActive ||
                  d.data.status === MomentTemplateStatus.SCHEDULED ||
                  isTemplateTemplates
                }
                color="primary"
                onClick={() => {
                  setModel(d);
                  onSwitchMode();
                }}
              >
                {t('Edit')}
              </Button>
            )}
            {errorHelper && (
              <FormHelperText error>{errorHelper}</FormHelperText>
            )}
          </FlexContainer>
          <Typography variant="caption" style={{ fontWeight: 'bolder' }}>
            {t('Title visible to admins only')}
          </Typography>
        </div>
      ) : (
        <FlexContainer>
          <TextField
            autoFocus={editActive}
            variant="standard"
            placeholder={t('[Internal title] Unnamed Moment')}
            value={x}
            onChange={(ev) => {
              setX(ev.target.value);
              setModel({
                ...model,
                data: {
                  ...model.data,
                  title: ev.target.value
                }
              });
            }}
            InputProps={{
              style: { fontSize: theme.custom.fontSize.xl, width: '400px' }
            }}
          />
          <Button
            disabled={otherEditActive}
            color="primary"
            onClick={onSwitchMode}
          >
            {t('Cancel')}
          </Button>

          {editActive && x.length >= MIN_TITLE_LENGTH && (
            <ButtonWithPromise
              pending={t('Saving...')}
              onClick={async () => await onSave({ ...model.data, title: x })}
              color="primary"
              disabled={!hasChanges}
            >
              {t('Save')}
            </ButtonWithPromise>
          )}
        </FlexContainer>
      )}
    </>
  );
};

type EditSectionButtonProps = {
  value: Doc<IMomentTemplate>;
  onSwitchMode: () => void;
  onCancel: () => void;
  editActive: boolean;
  otherEditActive: boolean;
  onSave: () => Promise<any>;
  hasChanges: boolean;
  isTemplateTemplates: boolean;
  sectionKey: string;
};

const EditableMomentSectionContainer = styled('div')<{ error?: boolean }>(
  (p) => ({
    border: p.theme.custom.border.standard,
    borderColor: p.error ? p.theme.palette.error.main : undefined,
    borderRadius: p.theme.custom.border.radius(),
    padding: p.theme.spacing(2),
    minWidth: p.theme.spacing(92) // not very responsive...
  })
);

const EditableMomentSectionBodyContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

const StandardEditButton = ({
  value,
  editActive,
  otherEditActive,
  onSave,
  onSwitchMode,
  hasChanges,
  onCancel,
  isTemplateTemplates,
  sectionKey
}: EditSectionButtonProps) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  if (value.data.status === MomentTemplateStatus.COMPLETE) {
    return null;
  }
  if (value.data.status !== MomentTemplateStatus.DRAFT) {
    return (
      <Tooltip title={`${t('Revert to Draft to edit')}`}>
        <Button variant="text" disabled={true}>
          {t('Edit')}
        </Button>
      </Tooltip>
    );
  }
  if (otherEditActive) {
    return (
      <Tooltip title={`${t('Save your changes to edit this section')}`}>
        <Button variant="text" disabled={true}>
          {t('Edit')}
        </Button>
      </Tooltip>
    );
  }
  if (editActive) {
    return (
      <FlexContainer justifyContent="flex-end" spacing={1}>
        <Button variant="text" onClick={() => onCancel()}>
          {t('Cancel')}
        </Button>

        <ButtonWithPromise
          onClick={() => onSave()}
          pending={t('Saving...')}
          variant="text"
          color="primary"
          disabled={!hasChanges}
        >
          {t('Save')}
        </ButtonWithPromise>
      </FlexContainer>
    );
  }
  return (
    <Button
      variant="text"
      color="primary"
      onClick={() => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Section Edit Clicked',
          Explanation:
            'Admin user clicked to edit a particular section of the Moment content/settings',
          'Moment ID': value.id,
          'Moment Name': value.data.title,
          'Section Title': sectionKey
        });
        onSwitchMode();
      }}
      disabled={isTemplateTemplates}
    >
      {t('Edit')}
    </Button>
  );
};

const EditableMomentSection = ({
  d,
  icon,
  title,
  description,
  editComponent,
  viewComponent,
  onSave,
  onSwitchMode,
  editButton,
  editActive,
  otherEditActive,
  showViewMode,
  errorText,
  model,
  setModel,
  hasChanges,
  tokens,
  isTemplateTemplates,
  sectionKey
}: {
  d: Doc<IMomentTemplate>;
  onSave: (update: Partial<IMomentTemplate>) => Promise<any>;
  icon: Icon;
  title: React.ReactNode;
  description: React.ReactNode;
  editComponent: ComponentType<EditComponentProps>;
  viewComponent: ComponentType<ViewComponentProps>;
  onSwitchMode: () => void;
  editButton: ComponentType<EditSectionButtonProps>;
  editActive: boolean;
  otherEditActive: boolean;
  showViewMode: boolean;
  errorText?: React.ReactNode;
  model: Doc<IMomentTemplate>;
  setModel: React.Dispatch<React.SetStateAction<Doc<IMomentTemplate>>>;
  hasChanges: boolean;
  tokens: TokenDefinition[];
  isTemplateTemplates: boolean;
  sectionKey: string;
}) => {
  const showView = showViewMode && !editActive;
  const save = () => onSave(model.data);

  return (
    <FlexContainer direction="column" alignItems="flex-start" spacing={0}>
      <EditableMomentSectionContainer error={!!errorText}>
        <FlexContainer justifyContent="space-between" alignItems="flex-start">
          <FlexContainer
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <FlexContainer spacing={1}>
              {createElement(icon, { size: 15 })}
              <Typography variant="h6">{title}</Typography>
            </FlexContainer>
            {showView ? (
              createElement(viewComponent, {
                value: d,
                onCancel: () => onSwitchMode(),
                tokens
              })
            ) : (
              <Typography variant="body1">{description}</Typography>
            )}
          </FlexContainer>
          {createElement(editButton, {
            value: model,
            onSwitchMode,
            editActive,
            otherEditActive,
            onSave: save,
            hasChanges,
            onCancel: () => {
              setModel(d); // this RESETS the model. if we don't do this people go to cancel, save another section and end up propagating the cancelled changes too
              onSwitchMode(); // banking on the fact that we're only here in edit mode
            },
            isTemplateTemplates,
            sectionKey
          })}
        </FlexContainer>
        {editActive && (
          <EditableMomentSectionBodyContainer>
            {createElement(editComponent, {
              value: model,
              onChange: setModel,
              // Not sure this is necessary here as onCancel is done in the editButton component
              onCancel: () => {
                setModel(d); // this RESETS the model. if we don't do this people go to cancel, save another section and end up propagating the cancelled changes too
                onSwitchMode(); // banking on the fact that we're only here in edit mode
              },
              onSave: save,
              hasChanges,
              tokens
            })}
          </EditableMomentSectionBodyContainer>
        )}
      </EditableMomentSectionContainer>
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </FlexContainer>
  );
};

const Body = ({ d }: { d: Doc<IMomentTemplate> }) => {
  const categoryEditEnabled = useFeatureEnabled('MOMENTS_CATEGORY_EDIT_V1');
  const { ROUTES, goTo } = useRoutes();
  const currentUser = useCurrentUser();
  const [errors, setErrors] = useState<Set<Validators>>(new Set());
  const [editModes, setEditModes] = useState<{ [key: string]: boolean }>({});
  const contentPreviewDialog = useDialogState();
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const [model, setModel] = useModel(d);
  const hasChanges = useMemo(() => !isEqual(d, model), [d, model]);
  const { trackEvent } = useProductAnalytics();

  const tokens = useTokenDefinitionsForMoment(
    currentUser.org.id,
    currentUser.org.customUserFields
  );
  const isTemplateTemplates = d.data.status === MomentTemplateStatus.TEMPLATE;

  const trackingFns = useMemo(() => {
    return {
      onDuplicate: (
        createdMomentId: string,
        duplicate: 'True' | 'False',
        OriginalMoment?: Doc<IMomentTemplate>
      ) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Created',
          Explanation: 'Admin user created a new Moment',
          'Moment ID': createdMomentId,
          Duplicate: duplicate,
          'Original Moment Id': OriginalMoment?.id || 'None',
          'Original Moment Name': OriginalMoment?.data.title || 'None',
          'Original Moment Status': OriginalMoment?.data.status || 'None'
        });
      },
      onRevert: (momentDoc: Doc<IMomentTemplate>) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Reverted To Draft',
          Explanation:
            'Admin user unscheduled a moment, putting it back into draft state',
          'Moment ID': momentDoc.id,
          'Moment Name': momentDoc.data.title
        });
      },
      onSchedule: (momentDoc: Doc<IMomentTemplate>) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Scheduled',
          Explanation: 'Admin User Scheduled a moment',
          'Moment ID': momentDoc.id,
          'Moment Name': momentDoc.data.title,
          'Send Date': formatDatePrecise(toMoment(momentDoc.data.sendDate.at)),
          'Relative Send Date': toMoment(momentDoc.data.sendDate.at).diff(
            moment.now(),
            'days'
          )
        });
      },
      onScheduleFailed: (momentDoc: Doc<IMomentTemplate>, errors: string[]) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Schedule Attempted',
          Explanation:
            'Admin user tried to schedule the Moment but errors prevented this',
          'Moment ID': momentDoc.id,
          'Moment Name': momentDoc.data.title,
          error: errors.join(', ')
        });
      }
    };
  }, [trackEvent]);

  const toggleEditMode = (key: string) =>
    setEditModes((x) => ({ ...x, [key]: !x[key] }));

  const isOtherEditActive = (key: string) =>
    !!Object.entries(editModes)
      .filter(([k]) => k !== key)
      .map(([k, v]) => v)
      .filter((t) => t).length;

  const save = (update: Partial<IMomentTemplate>) =>
    saveMomentTemplate(currentUser.id, updateDataInDoc(d, update));

  const getActiveEditSection = () => {
    for (const [key] of Object.entries(editModes)) {
      if (editModes[key]) {
        if (key === 'sendDate') {
          return 'send date';
        }
        if (key === 'audience') {
          return 'to';
        }
        return key;
      }
    }
    return '';
  };

  const saveAndCloseEditMode = (
    update: Partial<IMomentTemplate>,
    key: string
  ) =>
    save(update)
      .then((d) => {
        setEditModes((x) => ({ ...x, [key]: false }));
      })
      .catch((err) => console.log(err));

  const interceptorMessage = t(
    'Your moment hasn’t been saved. If you leave now, your changes will be lost.'
  );
  const shouldBlock = useCallback(
    (prev, next) => {
      const pathNameChange = !next || prev?.pathname !== next?.pathname;
      if (pathNameChange && model !== d) {
        return interceptorMessage;
      }
    },
    [model, d, interceptorMessage]
  );
  useBlockOnRouteChange(shouldBlock);

  const getNextErrors = (d: Doc<IMomentTemplate>) => {
    const nextErrors = new Set<Validators>();
    if (!d.data.title) {
      nextErrors.add('NO_TITLE');
    }
    if (!d.data.content.audiences.filter((a) => !a.inactive).length) {
      nextErrors.add('NO_AUDIENCE');
    }
    if (!d.data.replyTo?.email) {
      nextErrors.add('NO_REPLYTO_EMAIL');
    }
    if (!d.data.from.name) {
      nextErrors.add('NO_FROM_NAME');
    }

    if (toMoment(d.data.sendDate.at).tz(d.data.tz).isBefore()) {
      nextErrors.add('PAST_SEND_DATE');
    }
    if (!d.data.subject) {
      nextErrors.add('NO_SUBJECT');
    }
    if (
      d.data.content.audiences
        .filter((a) => !a.inactive)
        .some((a) => a.blocks.length === 0)
    ) {
      nextErrors.add('NO_TA_BLOCKS');
    }
    return nextErrors;
  };

  const fromSectionErrorMessageText = useMemo(() => {
    if (errors.has('NO_FROM_NAME') && errors.has('NO_REPLYTO_EMAIL')) {
      return t('Please add both a reply-to email address and sender name');
    }
    if (errors.has('NO_FROM_NAME')) {
      return t('Please add a sender name');
    }
    if (errors.has('NO_REPLYTO_EMAIL')) {
      return t('Please add a reply-to email address');
    }
    return null;
  }, [t, errors]);

  const checkSectionErrors = (hasValue: boolean, validator: Validators) => {
    setErrors((prevErrors) => {
      const newErrors = new Set([...prevErrors]);
      hasValue ? newErrors.delete(validator) : newErrors.add(validator);
      return newErrors;
    });
  };

  console.log('D: ', d);
  return (
    <PageBody noTopPadding>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <EditableTitle
            d={d}
            onSave={(partial) => {
              checkSectionErrors(!!partial.title, 'NO_TITLE');
              return saveAndCloseEditMode(partial, 'title');
            }}
            editActive={!isTemplateTemplates && !!editModes['title']}
            otherEditActive={isOtherEditActive('title')}
            onSwitchMode={() => toggleEditMode('title')}
            errorHelper={errors.has('NO_TITLE') && t('Please add a title')}
            model={model}
            setModel={setModel}
            hasChanges={hasChanges}
            isTemplateTemplates={isTemplateTemplates}
          />
        </PageToolbarTitle>
        <PageToolbarActions>
          <ButtonWithPromise
            variant="outlined"
            color="primary"
            pending={t('Duplicating...')}
            fullWidth
            onClick={async () => {
              console.log('Moments Dup: ');
              const moment = isTemplateTemplates
                ? await duplicateMomentTemplateTemplate(d, currentUser)
                : await duplicateMomentTemplate(d, currentUser.id);

              trackingFns.onDuplicate(moment.id, 'True', d);
              goTo(
                ROUTES.moments.details.url({
                  momentTemplateId: moment.id
                })
              );
            }}
          >
            {t('Duplicate')}
          </ButtonWithPromise>
          {!isTemplateTemplates &&
            d.data.status !== MomentTemplateStatus.COMPLETE && (
              <>
                {d.data.status !== MomentTemplateStatus.SCHEDULED ? (
                  <Tooltip
                    title={`${t(
                      "Please save your recent changes in the '{{active}}' section before scheduling",
                      {
                        active: capitalize(getActiveEditSection())
                      }
                    )}`}
                    disableHoverListener={!hasChanges}
                    placement="bottom"
                  >
                    <div>
                      <ButtonWithPromise
                        disabled={hasChanges}
                        variant="contained"
                        color="primary"
                        pending={t('Scheduling...')}
                        fullWidth
                        onClick={async () => {
                          const nextErrors = getNextErrors(d);
                          setErrors(nextErrors);
                          if (nextErrors.size) {
                            trackingFns.onScheduleFailed(
                              d,
                              Array.from(nextErrors)
                            );
                            return;
                          }
                          trackingFns.onSchedule(d);
                          await updateMomentTemplateStatus(
                            currentUser.id,
                            d,
                            MomentTemplateStatus.SCHEDULED
                          )
                            .then(() => {
                              enqueueSnackbarSuccess(
                                t('Moment has successfully been scheduled')
                              );
                              goTo(ROUTES.moments.overview.url());
                            })
                            .catch((e) => {
                              console.warn('Errored:', e);
                              enqueueSnackbarError(
                                t(
                                  'Something went wrong. Please try again later'
                                )
                              );
                            });
                        }}
                      >
                        {t('Schedule')}
                      </ButtonWithPromise>
                    </div>
                  </Tooltip>
                ) : (
                  <ButtonWithPromise
                    variant="contained"
                    color="primary"
                    pending={t('Reverting...')}
                    fullWidth
                    onClick={async () => {
                      trackingFns.onRevert(d);
                      await updateMomentTemplateStatus(
                        currentUser.id,
                        d,
                        MomentTemplateStatus.DRAFT
                      )
                        .then(() => {
                          enqueueSnackbarSuccess(
                            t('Moment has successfully been reverted')
                          );
                        })
                        .catch((e) => {
                          console.warn('Errored:', e);
                          enqueueSnackbarError(
                            t('Something went wrong. Please try again later')
                          );
                        });
                    }}
                  >
                    {t('Revert to draft')}
                  </ButtonWithPromise>
                )}
              </>
            )}
        </PageToolbarActions>
      </PageToolbar>

      <Section>
        <div>
          <Section spacing={2}>
            <FlexContainer justifyContent="flex-start" spacing={2}>
              <MomentTemplateStatusChip status={d.data.status} />
              {(d.data.status === MomentTemplateStatus.SCHEDULED ||
                d.data.status === MomentTemplateStatus.COMPLETE) && (
                <div>
                  {toMoment(d.data.sendDate.at)
                    .tz(d.data.tz)
                    .format(
                      `${currentUser.org.dateFormats.MMDDYYYY}, hh:mm a z`
                    )}
                </div>
              )}
            </FlexContainer>
          </Section>

          <FlexContainer
            direction="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            spacing={2}
          >
            <EditableMomentSection
              d={d}
              onSave={(partial) => {
                checkSectionErrors(
                  !!partial.content?.audiences.filter((a) => !a.inactive)
                    .length,
                  'NO_AUDIENCE'
                );
                return saveAndCloseEditMode(partial, 'audience');
              }}
              icon={Users}
              sectionKey="to"
              title={t('To')}
              description={t(
                'Create different audiences to show content to specific groups of people'
              )}
              editComponent={EditSectionAudience}
              viewComponent={ViewSectionAudience}
              editButton={StandardEditButton}
              editActive={!!editModes['audience']}
              otherEditActive={isOtherEditActive('audience')}
              onSwitchMode={() => toggleEditMode('audience')}
              showViewMode={!!d.data.content.audiences.length}
              errorText={
                errors.has('NO_AUDIENCE') &&
                t('Please set up at least once audience')
              }
              model={model}
              setModel={setModel}
              hasChanges={hasChanges}
              tokens={tokens}
              isTemplateTemplates={isTemplateTemplates}
            />
            {!isTemplateTemplates && (
              <EditableMomentSection
                d={d}
                onSave={(partial) => {
                  checkSectionErrors(!!partial.from?.name, 'NO_FROM_NAME');
                  checkSectionErrors(
                    !!partial.replyTo?.email,
                    'NO_REPLYTO_EMAIL'
                  );
                  return saveAndCloseEditMode(partial, 'from');
                }}
                icon={User}
                sectionKey="from"
                title={t('From')}
                description={t('Specify who this message will be from')}
                editComponent={EditSectionFrom}
                viewComponent={ViewSectionFrom}
                editButton={StandardEditButton}
                editActive={!!editModes['from']}
                otherEditActive={isOtherEditActive('from')}
                onSwitchMode={() => toggleEditMode('from')}
                showViewMode={!!(d.data.from.name || d.data.replyTo?.email)}
                errorText={fromSectionErrorMessageText}
                model={model}
                setModel={setModel}
                hasChanges={hasChanges}
                tokens={tokens}
                isTemplateTemplates={isTemplateTemplates}
              />
            )}

            {!isTemplateTemplates && (
              <EditableMomentSection
                d={d}
                onSave={(partial) => {
                  checkSectionErrors(!!partial.sendDate, 'PAST_SEND_DATE');
                  return saveAndCloseEditMode(partial, 'sendDate');
                }}
                icon={Clock}
                sectionKey="senddate"
                title={t('Send date')}
                description={t(
                  'Set when you want your message to be delivered'
                )}
                editComponent={EditSectionSendDate}
                viewComponent={ViewSectionSendDate}
                editButton={StandardEditButton}
                editActive={!!editModes['sendDate']}
                otherEditActive={isOtherEditActive('sendDate')}
                onSwitchMode={() => toggleEditMode('sendDate')}
                showViewMode={!!d.data.sendDate}
                errorText={
                  errors.has('PAST_SEND_DATE') &&
                  t('Please make sure your send date is in the future')
                }
                model={model}
                setModel={setModel}
                hasChanges={hasChanges}
                tokens={tokens}
                isTemplateTemplates={isTemplateTemplates}
              />
            )}

            <EditableMomentSection
              d={d}
              onSave={(partial) => {
                checkSectionErrors(!!partial.subject, 'NO_SUBJECT');
                return saveAndCloseEditMode(partial, 'subject');
              }}
              icon={Mail}
              sectionKey="subject"
              title={t('Subject')}
              description={t(
                'A subject line employees will see when they receive a message'
              )}
              editComponent={EditSectionSubject}
              viewComponent={ViewSectionSubject}
              editButton={StandardEditButton}
              editActive={!!editModes['subject']}
              otherEditActive={isOtherEditActive('subject')}
              onSwitchMode={() => toggleEditMode('subject')}
              showViewMode={!!d.data.subject}
              errorText={errors.has('NO_SUBJECT') && t('Please add a subject')}
              model={model}
              setModel={setModel}
              hasChanges={hasChanges}
              tokens={tokens}
              isTemplateTemplates={isTemplateTemplates}
            />

            <EditableMomentSection
              d={d}
              onSave={(partial) => {
                checkSectionErrors(
                  !!partial.content?.audiences.filter((a) => !a.inactive)
                    .length,
                  'NO_AUDIENCE'
                );
                checkSectionErrors(
                  !partial.content?.audiences
                    .filter((a) => !a.inactive)
                    .some((a) => a.blocks.length === 0),
                  'NO_TA_BLOCKS'
                );
                return saveAndCloseEditMode(partial, 'content');
              }}
              icon={Image}
              sectionKey="content"
              title={t('Content')}
              description={t('Create and manage the content for your message')}
              editComponent={
                /* different component in non-draft state */ EditSectionContent
              }
              viewComponent={ViewSectionContent}
              editButton={
                d.data.status === MomentTemplateStatus.DRAFT
                  ? StandardEditButton
                  : () => (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          trackEvent({
                            eventType: PAEventType.OTHER,
                            name: 'Moment Content Preview Clicked',
                            Explanation:
                              'Admin user clicked to preview the content of a scheduled Moment',
                            'Moment ID': d.id,
                            'Moment Name': d.data.title
                          });
                          contentPreviewDialog.openDialog();
                        }}
                      >
                        {t('Preview')}
                      </Button>
                    )
              }
              editActive={!!editModes['content']}
              otherEditActive={isOtherEditActive('content')}
              onSwitchMode={() => {
                // This is a hack, as this component has a special view mode, which
                // is closed by calling this function...
                if (contentPreviewDialog.dialogOpen) {
                  contentPreviewDialog.closeDialog();
                } else {
                  toggleEditMode('content');
                }
              }}
              showViewMode={contentPreviewDialog.dialogOpen}
              errorText={
                errors.has('NO_TA_BLOCKS') &&
                t(
                  'Please add content for all your target audiences, or delete unneeded audiences'
                )
              }
              model={model}
              setModel={setModel}
              hasChanges={hasChanges}
              tokens={tokens}
              isTemplateTemplates={isTemplateTemplates}
            />
            {categoryEditEnabled && (
              <EditableMomentSection
                d={d}
                onSave={(partial) => {
                  checkSectionErrors(!!partial.sendDate, 'CATEGORIES');
                  return saveAndCloseEditMode(partial, 'category');
                }}
                icon={Component}
                title={t('Categories')}
                description={t(
                  'Set which categories this moment should be a part of'
                )}
                editComponent={EditSectionCategories}
                viewComponent={ViewSectionCategories}
                editButton={StandardEditButton}
                editActive={!!editModes['category']}
                otherEditActive={isOtherEditActive('category')}
                onSwitchMode={() => toggleEditMode('category')}
                showViewMode={!!d.data.categories.length}
                errorText={
                  errors.has('CATEGORIES') &&
                  t('Please make sure you;ve selected at east one category')
                }
                model={model}
                setModel={setModel}
                hasChanges={hasChanges}
                tokens={tokens}
                isTemplateTemplates={isTemplateTemplates}
                sectionKey="categories"
              />
            )}
            {!isTemplateTemplates && (
              <DeleteButton
                variant="outlined"
                color="secondary"
                onDelete={async () => {
                  goTo(ROUTES.moments.overview.url());
                  await deleteMomentTemplate(d);
                }}
              >
                {t('Delete moment')}
              </DeleteButton>
            )}
          </FlexContainer>
        </div>
      </Section>
    </PageBody>
  );
};

const MomentDetails = ({ momentTemplateId }: { momentTemplateId: string }) => {
  const [moment, loading, error] = useMomentTemplateById(momentTemplateId);
  const { trackEvent } = useProductAnalytics();

  useEffect(() => {
    if (!moment) return;

    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Moment Page Loaded',
      Explanation: 'Admin user loaded the view/edit page for a Moment',
      'Moment ID': moment.id,
      'Moment Name': moment.data.title,
      'Moment State': moment.data.status
    });
  }, [trackEvent, moment]);

  if (loading) {
    return <PageLoader />;
  }
  if (error || !moment) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'moment_details', section: 'custom_moment' }}
      />
    );
  }
  return <Body d={moment} />;
};

const TemplateMomentDetails = ({
  momentTemplateId
}: {
  momentTemplateId: string;
}) => {
  const [moment, loading, error] = useMomentTemplatesTemplateById(
    momentTemplateId
  );
  const { trackEvent } = useProductAnalytics();

  // TODO - this will also be another kind of event eventually - more template related
  useEffect(() => {
    if (!moment) return;

    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Moment Page Loaded',
      Explanation: 'Admin user loaded the view/edit page for a Moment',
      'Moment ID': moment.id,
      'Moment Name': moment.data.title,
      'Moment State': moment.data.status
    });
  }, [trackEvent, moment]);

  if (loading) {
    return <PageLoader />;
  }
  if (error || !moment) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'moment_details', section: 'template_moment' }}
      />
    );
  }
  return <Body d={moment} />;
};

export const PageMomentDetails = ({
  momentTemplateId
}: {
  momentTemplateId: string;
}) => {
  if (isMomentTemplateTemplateId(momentTemplateId)) {
    return <TemplateMomentDetails momentTemplateId={momentTemplateId} />;
  }

  return <MomentDetails momentTemplateId={momentTemplateId} />;
};
